import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APICommonService } from '@nationwide/internet-servicing-angular-services';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { EnvironmentpickerService } from '../../../shared/services/environmentpicker.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { TokenDataService } from '../../../shared/services/token-data.service';
import { UtilService } from '../../../shared/services/util.service';

type AggregateLimitValue = number | string | null;

interface AggregateLimitItem {
    aggregateLimit?: AggregateLimitValue;
    depletionAmount?: AggregateLimitValue;
    maintenanceDedAmt?: AggregateLimitValue;
    limitAmount?: AggregateLimitValue;
    thresholdAmount?: AggregateLimitValue;
    usedAmount?: AggregateLimitValue;
}

interface AggregateLimitsResponseBody {
    aggregateLimit?: AggregateLimitValue;
    aggregateLimits?: AggregateLimitItem[];
    depletionAmount?: AggregateLimitValue;
    maintenanceDedAmt?: AggregateLimitValue;
    limitAmount?: AggregateLimitValue;
    thresholdAmount?: AggregateLimitValue;
    usedAmount?: AggregateLimitValue;
}

export interface AggregateLimitsResponse {
    body?: AggregateLimitsResponseBody;
}

@Injectable({ providedIn: 'root' })
export class AggregateLimitsService {
    constructor(
        private httpClient: HttpClient,
        private apiCommonService: APICommonService,
        private tokenDataService: TokenDataService,
        private util: UtilService,
        private environmentPicker: EnvironmentpickerService,
        private logger: LoggerService
    ) {
        this.logger.setModuleName('AggregateLimitsService');
    }

    getAggregateLimits(claimNumber: string, policyPublicID: string, policyNumber?: string): Observable<AggregateLimitsResponse> {
        const aggregateLimitsUrl = `${this.environmentPicker.getEnvironmentClaimsCenterApiURL() + environment.ClaimsAPIs.claimsPolicy}claims/${claimNumber}/policy/${policyPublicID}/aggregate-limits`;
        const appClientId = environment.appProps.appClientId;
        const authToken = this.tokenDataService.getToken();
        const sessionId = window.sessionStorage.getItem('sessionId');
        let headers = this.apiCommonService.buildHeader({
            appClientId,
            authToken,
            contentType: 'application/json',
            headerType: 'X-NW-Message-ID',
            sessionId: sessionId ?? undefined
        });

        const serverId = this.environmentPicker.getEnvironmentServerNumber();
        if (serverId) {
            headers = headers.append('X-NW-Server-ID-Number', serverId);
        }
        headers = headers.append('X-NW-Message-ID', this.apiCommonService.generateTransactionId());
        headers = headers.delete('x-nw-sessionid');

        const userId = this.getUserId();
        this.logger.info(`Calling aggregate limits API for claim "${claimNumber}" and policyPublicID "${policyPublicID}" - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`);
        this.logger.debug(`Request URL: "${aggregateLimitsUrl}" - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`);

        return this.httpClient.get<AggregateLimitsResponse>(aggregateLimitsUrl, { headers, observe: 'response' }).pipe(
            tap(() => this.logger.info(`Aggregate limits API succeeded for claim "${claimNumber}" - userId: ${userId}, policyNumber: ${policyNumber || 'N/A'}`)),
            catchError((error: HttpErrorResponse) =>
                this.util.handleError(`Error retrieving aggregate limits with URL: "${aggregateLimitsUrl}"`, error))
        );
    }

    private getUserId(): string {
        return window.sessionStorage.getItem('userId') ?? '';
    }
}
